import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Collapse from "@mui/material/Collapse"; // Import Collapse component
import { IconButton, Typography } from "@mui/material";
import { sortingarrowheads } from "../../assets/Icons";

const StyledTableRow = styled(TableRow)(({ theme, index }) => ({
  backgroundColor: index % 2 === 0 ? theme.palette.action.hover : "white",
  "&:hover": {
    transform: "scale(0.99)",
    overflowX: "hidden",
    transition: "transform 0.2s ease",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function GenericTable({
  columns,
  rows,
  maxHeight = 440,
  rowCount,
  cb,
  addCb,
  collapsibleRows,
  handleNameClickCb,
  disabledStatusFilter
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null); // State to track the expanded row

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRowClick = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
  };

  const sortedRows = [...rows].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key] || "";
    const bValue = b[sortConfig.key] || "";

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (columnId) => {
    let direction = "asc";
    if (sortConfig.key === columnId && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: columnId, direction });
  };

  const pageWiseRecords = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Fab
        color="secondary"
        aria-label="add"
        className="d-block d-md-none"
        onClick={addCb}
        sx={{ position: "absolute", bottom: 50, right: 0 }}
      >
        <AddIcon />
      </Fab>
      <TableContainer sx={{ maxHeight, minHeight: "60vh", marginTop: '5px', padding: '10px' }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              {/* Checkbox Column */}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ fontSize: "1rem", fontWeight: 700, padding: "8px" }}
                >
                  <IconButton
                    sx={{ padding: 0, color: "inherit", display: "flex", alignItems: "center" }}
                    onClick={() => handleSort(column.id)}
                  >
                    <Typography sx={{ color: "inherit", fontSize: "1rem", fontWeight: 700 }}>
                      {column.label}
                    </Typography>
                    {column.id !== "lastTimeActivity" && column.id !== "action" && (
                      <img
                        src={sortingarrowheads}
                        alt="Sort"
                        width={20}
                        height={20}
                        style={{
                          marginLeft: 5,
                          transform: sortConfig.key === column.id && sortConfig.direction === "desc" ? "rotate(180deg)" : "none",
                        }}
                      />
                    )}
                  </IconButton>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
          {pageWiseRecords.map((row, rowIndex) => {
              return (
                <React.Fragment key={`row_idx_${rowIndex}`}>
                  {/* Main row */}
                  <StyledTableRow
                    hover
                    key={`row_idx_${page*rowIndex}`}
                    index={rowIndex}
                    role="checkbox"
                    tabIndex={-1}
                    style={{ border: '10px solid green' }}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align} onClick={() => collapsibleRows && column.id === 'action' && row?.action?.props?.children === 'More Details' && handleRowClick(rowIndex)}>
                          {typeof value === "function" ||
                            React.isValidElement(value)
                            ? value
                            : column.format && typeof value === "number"
                              ? column.format(value)
                              : column.id === 'patientName' ?
                                (
                                  <div className="rowHeading" onClick={() => handleNameClickCb(row)}>
                                    <strong>
                                      {value}
                                    </strong>
                                  </div>
                                )
                                : column.highlight ?
                                  <span style={{ backgroundColor: column.color, paddingTop: 5, paddingBottom: 5, paddingLeft: 10, paddingRight: 10,  borderRadius: 13 }}>{value}</span>
                                  : value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>

                  {/* Collapsible row content */}
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={expandedRow === rowIndex} timeout="auto" unmountOnExit>
                        {row.callapseElement}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rowCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className="align-pagination"
      />
    </Paper>
  );
}
